import React, { useEffect, useRef, useState } from "react";
import Glide from "@glidejs/glide";
import PrimaryBtn from "@/components/Buttons/PrimaryButton";
import linkedinIcon from "@/assets/team/linkedin-purple.png";
import { FaCircleArrowLeft, FaCircleArrowRight } from "react-icons/fa6";
import stripTags from "@/functions/stripTags";
import IconEmail from "@/assets/icons/icon-email.svg";
import IconPhone from "@/assets/icons/icon-phone.svg";

export default function ModalTeamMember({
  activeContent,
  getActiveMembers,
  handleClose,
}) {
  const [activeMemberIndex, setActiveMemberIndex] = useState(
    getActiveMembers.findIndex((member) => member.id === activeContent),
  );
  const member = getActiveMembers[activeMemberIndex];

  const glideContainerRef = useRef(null);
  const glideRef = useRef(null);

  useEffect(() => {
    if (glideContainerRef.current) {
      const glideInstance = new Glide(glideContainerRef.current, {
        type: "slider",
        perView: 1,
        peek: {
          before: 30,
          after: 30,
        },
        breakpoints: {
          800: {
            perView: 1,
            peek: {
              before: 10,
              after: 10,
            },
          },
        },
        gap: 10,
        rewind: false,
      });

      glideInstance.on("move.after", () => {
        setActiveMemberIndex(glideInstance.index);
      });

      glideInstance.mount();
      glideRef.current = glideInstance;

      return () => {
        glideInstance.destroy();
      };
    }
  }, []);

  useEffect(() => {
    if (glideRef.current) {
      glideRef.current.go(`=${activeMemberIndex}`);
    }
  }, [activeMemberIndex]);

  const handleSlideClick = (index) => {
    setActiveMemberIndex(index);
  };

  const handleNext = () => {
    if (glideRef.current) {
      glideRef.current.go(">");
    }
  };

  const handlePrev = () => {
    if (glideRef.current) {
      glideRef.current.go("<");
    }
  };

  return (
    <div className="bg-purple min-h-screen modal-team-member">
      <div className="flex container max-w-[1440px] h-full justify-center gap-10 md:gap-[100px] flex-wrap lg:flex-nowrap lg:pr-20 m-auto pt-20 pb-[53px]">
        <div className="max-w-[500px] w-full relative gap-10 flex-col-reverse justify-between">
          <div className="glide" ref={glideContainerRef}>
            <div className="glide__track" data-glide-el="track">
              <ul className="glide__slides">
                {getActiveMembers.map((member, index) => (
                  <li
                    className={`glide__slide ${
                      index === activeMemberIndex ? "active" : "inactive"
                    }`}
                    key={member.id}
                    onClick={() => handleSlideClick(index)}
                  >
                    <img
                      src={
                        member.profile_picture
                          ? member.profile_picture
                          : "/site-assets/placeholders/img-placeholder.jpg"
                      }
                      alt="Member"
                      className={`${
                        index === activeMemberIndex
                          ? "active-image"
                          : "inactive-image"
                      }`}
                    />
                  </li>
                ))}
              </ul>
            </div>
          </div>
          <div
            data-glide-el="controls"
            className="flex justify-between flex-wrap bottom-[-20px] right-0 relative z-[1000]"
          >
            <button
              className="text-white text-sm sm:text-base md:text-lg lg:text-xl/[30px] border-none z-[1000]"
              onClick={handleClose}
            >
              Close
            </button>
            <div className="flex gap-5">
              <button
                className="glide__arrow--left rounded-full text-5xl text-black h-50 "
                data-glide-dir="<"
                onClick={handlePrev}
              >
                <FaCircleArrowLeft stroke="purple" fill="white" />
              </button>
              <button
                className="glide__arrow--right rounded-full text-5xl text-black"
                data-glide-dir=">"
                onClick={handleNext}
              >
                <FaCircleArrowRight stroke="purple" fill="white" />
              </button>
            </div>
          </div>
        </div>

        <div className="max-w-[737px] w-full">
          <div className="flex flex-col items-start border-b-[1px] border-grey-border transition ease-in-out duration-300">
            <h3
              className="text-pink text-base md:text-3xl lg:text-[40px]/[56px] transition ease-in-out duration-300"
              dangerouslySetInnerHTML={{ __html: stripTags(member.title) }}
            />
            <span
              className="text-white text-sm sm:text-base md:text-lg lg:text-xl/[28px] pb-4 transition ease-in-out duration-300"
              dangerouslySetInnerHTML={{ __html: stripTags(member.role) }}
            />
          </div>
          <div className="flex justify-between items-center py-[45px] border-b-[1px] border-grey-border flex-wrap gap-6 transition ease-in-out duration-300">
            <div className="flex flex-col gap-[7px]">
              <div className="flex gap-[26px] items-center">
                <IconEmail />
                <span
                  className="text-sm sm:text-base md:text-lg lg:text-xl/[25px] transition ease-in-out duration-300"
                  dangerouslySetInnerHTML={{ __html: stripTags(member.email) }}
                />
              </div>
              <div className="flex gap-[26px] items-center">
                <IconPhone className="ml-[3px]" />
                <span
                  className="text-sm sm:text-base md:text-lg lg:text-xl/[25px] transition ease-in-out duration-300"
                  dangerouslySetInnerHTML={{
                    __html: stripTags(member.telephone),
                  }}
                />
              </div>
            </div>
            <div>
              <PrimaryBtn
                bgColor="pink"
                color="purple"
                href={member.linkedIn}
                className="hover:bg-white transition ease-in-out duration-300"
              >
                <div className="flex gap-5 w-[164px] items-center justify-center h-[54px]">
                  <span>Connect</span>
                  <img src={linkedinIcon ?? ""} alt="linkedin" />
                </div>
              </PrimaryBtn>
            </div>
          </div>
          <div className="flex flex-col gap-2 items-start pb-10 sm:pb-[80px] pt-[33px] transition ease-in-out duration-300">
            <span className="text-base text-pink">My specialism:</span>
            <p
              className="text-sm sm:text-base md:text-lg lg:text-xl/[30px] sm:min-h-[60px] text-start transition ease-in-out duration-300"
              dangerouslySetInnerHTML={{ __html: stripTags(member.specialism) }}
            />
          </div>
          <div className="flex flex-col gap-2 items-start border-b-[1px] border-grey-border pb-10 transition ease-in-out duration-300">
            <span className="text-base text-pink">Regions I operate in:</span>
            <p
              className="text-sm sm:text-base md:text-lg lg:text-xl/[30px] text-start transition ease-in-out duration-300"
              dangerouslySetInnerHTML={{ __html: stripTags(member.region) }}
            />
          </div>
          <div className="grid grid-cols-2 gap-y-6 pt-10 transition ease-in-out duration-300">
            <div className="flex flex-col gap-1">
              <p className="text-base/6 text-pink text-start">Favourite Food</p>
              <p
                className="text-sm sm:text-base md:text-lg lg:text-xl/[30px] text-start transition ease-in-out duration-300"
                dangerouslySetInnerHTML={{ __html: stripTags(member.food) }}
              />
            </div>
            <div className="flex flex-col gap-1">
              <p className="text-base/6 text-pink text-start">Top hobby</p>
              <p
                className="text-sm sm:text-base md:text-lg lg:text-xl/[30px] text-start transition ease-in-out duration-300"
                dangerouslySetInnerHTML={{ __html: stripTags(member.hobby) }}
              />
            </div>
            <div className="flex flex-col gap-1">
              <p className="text-base/6 text-pink text-start">
                Favourite TV show/Movie
              </p>
              <p
                className="text-sm sm:text-base md:text-lg lg:text-xl/[30px] text-start transition ease-in-out duration-300"
                dangerouslySetInnerHTML={{ __html: stripTags(member.movie) }}
              />
            </div>
            <div className="flex flex-col gap-1">
              <p className="text-base/6 text-pink text-start">
                Favourite music
              </p>
              <p
                className="text-sm sm:text-base md:text-lg lg:text-xl/[30px] text-start transition ease-in-out duration-300"
                dangerouslySetInnerHTML={{ __html: stripTags(member.music) }}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
